// @ts-check

/**
 * The shared tab strip. Every tab strip in the app renders through this
 * component — do not fork it (see the UI & Branding rules in CLAUDE.md).
 *
 * Extracted from the private `TabBar` in `InternalAuditModal.jsx`, which was
 * the only real tab component in the product; 27 other files hand-rolled
 * their own `role="tablist"` markup or a `TABS.map()`, which is why two
 * screens in the same module never looked alike.
 *
 * ## Two variants, because there are two jobs
 *
 * `underline` (default) — **section navigation inside one record.** A fixed,
 * small set of named sections of the thing you already opened (Details /
 * Assessments / Conclusion / Report). Order is meaningful, exactly one is
 * always active, and the set does not change while you look at it.
 *
 * `chip` — **a filter facet over a collection.** A variable, often wrapping
 * set derived from data (domains, categories, statuses), usually carrying a
 * count per option and usually offering an "all" option. `GapAnalysisDetail`
 * is the canonical example.
 *
 * These are deliberately NOT collapsed into one look. They answer different
 * questions — "which part of this record?" versus "which subset of these
 * rows?" — and a user who has learned that an underline means "you are
 * inside a record" should not meet the same shape doing filtering.
 *
 * @typedef {Object} TabItem
 * @property {string | number | null} id  Tab identity. `null` is allowed so a
 *   chip strip can express an "All" option without a sentinel string, and a
 *   number so an index-keyed strip (`tab === 0`) migrates without rewriting
 *   its body conditionals.
 * @property {import('react').ReactNode} label
 * @property {number} [count]        Rendered in the mono font after the
 *   label. Prefer this over baking `(3)` into `label` — a count is data, and
 *   a design change to how counts read should reach every strip at once.
 * @property {string} [color]        Chip variant only: a CSS custom-property
 *   reference (`var(--cat-sky)`) overriding the accent for this one option,
 *   for strips whose options carry their own colour (audit domains).
 * @property {boolean} [disabled]
 * @property {string} [title]        Native tooltip.
 *
 * @param {Object} props
 * @param {TabItem[]} props.tabs
 * @param {string | number | null} props.active
 * @param {(id: any) => void} props.onChange
 * @param {'underline' | 'chip'} [props.variant]
 * @param {string} [props.ariaLabel]  Names the strip for screen readers.
 *   Required in spirit — a page with two strips is ambiguous without it.
 * @param {import('react').CSSProperties} [props.style]
 */
export default function Tabs({ tabs, active, onChange, variant = 'underline', ariaLabel, style }) {
  const isChip = variant === 'chip';

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        ...(isChip
          ? { gap: 8, flexWrap: 'wrap' }
          : {
              gap: 0,
              marginBottom: 20,
              // A strip wider than its container scrolls sideways rather than
              // pushing tabs off-screen with no way to reach them — at 375px
              // eight Controls sections were unreachable (GH #7317). The base
              // line is an inset shadow, not a border: a scroll container
              // clips its children at the padding edge, so the tabs' old
              // -1px overlap onto a border would be cut off (and would itself
              // overflow vertically into a scrollbar).
              boxShadow: 'inset 0 -1px 0 var(--border)',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'thin',
            }),
        ...style,
      }}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.id;
        const accent = tab.color || 'var(--accent)';

        return (
          <button
            key={String(tab.id)}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={tab.disabled}
            title={tab.title}
            onClick={() => onChange(tab.id)}
            className={isChip ? 'chip' : undefined}
            style={
              isChip
                ? {
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: tab.disabled ? 'not-allowed' : 'pointer',
                    opacity: tab.disabled ? 0.5 : 1,
                    fontWeight: isActive ? 700 : 500,
                    background: isActive ? 'var(--accent-bg)' : 'var(--bg3)',
                    color: isActive ? accent : 'var(--fg3)',
                    border: `1px solid ${isActive ? 'var(--accent-border)' : 'var(--border)'}`,
                  }
                : {
                    background: 'none',
                    border: 'none',
                    cursor: tab.disabled ? 'not-allowed' : 'pointer',
                    opacity: tab.disabled ? 0.5 : 1,
                    padding: '8px 18px',
                    fontSize: 13,
                    fontWeight: 600,
                    color: isActive ? accent : 'var(--fg3)',
                    borderBottom: `2px solid ${isActive ? accent : 'transparent'}`,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    transition: 'color 0.12s',
                  }
            }
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ fontFamily: 'var(--font-mono)', marginLeft: isChip ? 0 : 6 }}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
